import { LibraryItem } from '../types';
import { PortfolioAiVersion, PortfolioEntry, visibleAiCreations, visiblePortfolioPhotos, portfolioEntryMatchesSlot } from '../../squads/classroom/types';
import { ResolvedSessionSlot } from '../../squads/utils/sessionPlan';

export function lessonSource(entry: PortfolioEntry) {
  return {
    sourcePortfolioEntryId: entry.id,
    sourceLesson: { sessionId: entry.sessionId, levelId: entry.levelId,
      ...(entry.slotIndex !== undefined ? { slotIndex: entry.slotIndex } : {}),
      ...(entry.date ? { date: entry.date } : {}),
    },
  };
}

export function cardsForCreation(cards: LibraryItem[], entry: PortfolioEntry, creationId: string): LibraryItem[] {
  return cards.filter(card => card.squadId === entry.squadId && (!card.teamId || card.teamId === entry.teamId)
    && (card.sourcePortfolioCreationId === creationId || card.shotBatch?.videos.some(video => video.id === creationId)));
}

export function cardsForEntry(cards: LibraryItem[], entry: PortfolioEntry): LibraryItem[] {
  const ids = new Set(entry.aiVersions.map(creation => creation.id));
  const origins = new Set([entry.id, ...entry.aiVersions.map(creation => creation.originEntryId).filter(Boolean)]);
  return cards.filter(card => card.squadId === entry.squadId && (!card.teamId || card.teamId === entry.teamId) && (
    (card.sourcePortfolioEntryId && origins.has(card.sourcePortfolioEntryId)) ||
    (card.sourcePortfolioCreationId && ids.has(card.sourcePortfolioCreationId)) ||
    card.shotBatch?.videos.some(video => ids.has(video.id)) ||
    card.sourcePortfolioCreationId === `shot_batch_${entry.id}`
  ));
}

export type ReadinessStatus = 'published' | 'pending' | 'missing';
export interface CardReadinessRow {
  id: string; title: string; entry?: PortfolioEntry; creation?: PortfolioAiVersion;
  cards: LibraryItem[]; status: ReadinessStatus; reason: string; eligible: boolean;
}
export const canNominateCreation = (creation: PortfolioAiVersion) =>
  ['image', 'video', 'audio', 'game_link', 'claude_artifact', 'link'].includes(creation.type || '')
    ? Boolean(creation.url) : creation.type === 'model_3d' && Boolean(creation.posterUrl);

export function buildCardReadiness(entries: PortfolioEntry[], cards: LibraryItem[], slots: ResolvedSessionSlot[]): CardReadinessRow[] {
  const rows: CardReadinessRow[] = [];
  const matched = new Set<string>();
  const selectedEntries = entries.filter(entry => slots.some(slot => portfolioEntryMatchesSlot(entry, slot)));
  selectedEntries.forEach(entry => {
    const creations = visibleAiCreations(entry.aiVersions);
    creations.forEach(creation => {
      const related = cardsForCreation(cards, entry, creation.id);
      related.forEach(card => matched.add(card.id));
      const published = related.filter(card => card.status === 'published');
      const pending = related.filter(card => ['nominated', 'review'].includes(card.status));
      const status = published.length ? 'published' : pending.length ? 'pending' : 'missing';
      rows.push({ id: `${entry.id}:${creation.id}`, title: creation.caption || creation.filename || 'Untitled creation', entry, creation,
        cards: published.length ? published : pending, status, eligible: status === 'missing' && canNominateCreation(creation) && !related.some(card => card.status === 'archived'),
        reason: status === 'published' ? 'Included in the PDF.' : status === 'pending' ? 'Not included: awaiting publication.'
          : related.some(card => card.status === 'archived') ? 'Not included: the Universe card was archived. Ask an admin to review it.'
          : canNominateCreation(creation) ? 'Not included: not nominated.' : 'Not included: add a supported image, video, audio, or game preview first.',
      });
    });
    if (!creations.length && visiblePortfolioPhotos(entry.photos).length) rows.push({ id: `${entry.id}:photos`, title: 'Student photos only', entry, cards: [], status: 'missing', eligible: false, reason: 'Not included: no AI creation has been added for nomination.' });
  });
  // New lesson references also retain cards whose original portfolio upload is no longer visible.
  cards.filter(card => !matched.has(card.id) && ((card.sourceLesson && slots.some(slot => portfolioEntryMatchesSlot(card.sourceLesson!, slot))) || selectedEntries.some(entry => cardsForEntry([card], entry).length))).forEach(card => {
    rows.push({ id: `card:${card.id}`, title: card.title, cards: [card], status: card.status === 'published' ? 'published' : ['nominated', 'review'].includes(card.status) ? 'pending' : 'missing', eligible: false,
      reason: card.status === 'published' ? 'Included in the PDF.' : card.status === 'archived' ? 'Not included: card archived.' : 'Not included: awaiting publication.' });
  });
  return rows;
}

export function printableCards(rows: CardReadinessRow[]): LibraryItem[] {
  return Array.from(new Map(rows.flatMap(row => row.cards.filter(card => card.status === 'published')).map(card => [card.id, card])).values());
}
export const universeCardUrl = (id: string) => `https://www.overtureai.org/universe?card=${encodeURIComponent(id)}`;
